import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/components/AuthProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PasswordRecoveryRedirect } from "@/components/PasswordRecoveryRedirect";
import { PageSkeleton } from "@/components/PageSkeleton";
import { PortfolioSkeleton } from "@/components/skeletons/PortfolioSkeleton";
import { BlogSkeleton } from "@/components/skeletons/BlogSkeleton";
import { DynamicTheme } from "@/components/DynamicTheme";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { Layout } from "@/components/layout/Layout";
import { ResetHandler } from "@/components/ResetHandler";

// Eagerly load homepage for faster initial render
import Index from "./pages/Index";
const Portfolio = lazy(() => import("./pages/Portfolio"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Auth = lazy(() => import("./pages/Auth"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Admin = lazy(() => import("./pages/Admin"));
const Webtools = lazy(() => import("./pages/Webtools"));
const WebtoolDetail = lazy(() => import("./pages/WebtoolDetail"));
const DemoViewer = lazy(() => import("./pages/DemoViewer"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Store = lazy(() => import("./pages/Store"));
const StoreProduct = lazy(() => import("./pages/StoreProduct"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ConfirmSubscription = lazy(() => import("./pages/ConfirmSubscription"));

const queryClient = new QueryClient();

// Prefetch component to load common routes after initial render
const RoutePrefetcher = () => {
  useEffect(() => {
    const prefetchRoutes = async () => {
      await Promise.all([
        import("./pages/Portfolio"),
        import("./pages/Blog"),
        import("./pages/About"),
        import("./pages/Webtools"),
      ]);
    };
    // Wait until the page has settled and the browser is idle, and skip it on data saver / slow connections.
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData || (conn?.effectiveType && conn.effectiveType !== "4g")) return;
    const timer = setTimeout(() => {
      if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(prefetchRoutes, { timeout: 5000 });
      else prefetchRoutes();
    }, 6000);
    return () => clearTimeout(timer);
  }, []);
  return null;
};

const App = () => (
  <ThemeProvider defaultTheme="system" storageKey="portfolio-theme">
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LazyMotion features={domAnimation} strict>
        <AuthProvider>
          <TooltipProvider>
            <DynamicTheme />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AnalyticsProvider>
                <ResetHandler />
                <ScrollToTop />
                <PasswordRecoveryRedirect />
                <RoutePrefetcher />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route
                    path="/portfolio"
                    element={
                      <Suspense fallback={<Layout><PortfolioSkeleton /></Layout>}>
                        <Portfolio />
                      </Suspense>
                    }
                  />
                  <Route path="/portfolio/:slug" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><ProjectDetail /></Suspense>} />
                  <Route
                    path="/blog"
                    element={
                      <Suspense fallback={<Layout><BlogSkeleton /></Layout>}>
                        <Blog />
                      </Suspense>
                    }
                  />
                  <Route path="/blog/:slug" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><BlogPost /></Suspense>} />
                  <Route path="/about" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><About /></Suspense>} />
                  <Route path="/auth" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Auth /></Suspense>} />
                  <Route path="/oauth/consent" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><OAuthConsent /></Suspense>} />
                  <Route path="/dashboard" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Admin /></Suspense>} />
                  <Route path="/webtools" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Webtools /></Suspense>} />
                  <Route path="/webtools/:slug" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><WebtoolDetail /></Suspense>} />
                  <Route path="/demo-viewer" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><DemoViewer /></Suspense>} />
                  <Route path="/search" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><SearchResults /></Suspense>} />
                  <Route path="/sitemap" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Sitemap /></Suspense>} />
                  <Route path="/unsubscribe" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Unsubscribe /></Suspense>} />
                  <Route path="/confirm-subscription" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><ConfirmSubscription /></Suspense>} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="/store" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><Store /></Suspense>} />
                  <Route path="/store/:slug" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><StoreProduct /></Suspense>} />
                  <Route path="*" element={<Suspense fallback={<Layout><PageSkeleton /></Layout>}><NotFound /></Suspense>} />
                </Routes>
              </AnalyticsProvider>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
        </LazyMotion>
      </QueryClientProvider>
    </HelmetProvider>
  </ThemeProvider>
);

export default App;
